
'use client';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { ISection } from '../../models/ISection';
import SectionEight from '../sections/SectionEight';
import SectionFive from '../sections/SectionFive';
import SectionFour from '../sections/SectionFour';
import SectionSix from '../sections/SectionSix';
import SectionTwo from '../sections/SectionTwo';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { IPaginatedData } from '../../models/ICommon';
import { apiService } from '../../services/ApiService';
import SectionNine from '../sections/SectionNine';
import SectionSeven from '../sections/SectionSeven';
import SectionThree from '../sections/SectionThree';
import Loader from './svg/Loader';
import { IBanner } from '../../models/IBanner';
import SectionOne from '../sections/SectionOne';
// import SectionOne from '../sections/SectionOne';

const templateComponentsMap: (props: any, id: string) => ({ [templateId: string]: ReactNode }) = (props: any, id: string) => ({
  6: <SectionOne {...props} key={props} />,
  16: <SectionTwo {...props} key={id} />,
  17: <SectionThree {...props} key={id} />,
  18: <SectionFour {...props} key={id} />,
  19: <SectionFive {...props} key={id}/>,
  20: <SectionSix {...props} key={id}/>,
  21: <SectionSeven {...props} key={id}/>,
  22: <SectionEight {...props} key={id}/>,
  23: <SectionNine {...props} key={id}/>,
 
})


interface Props {
  sections: IPaginatedData<ISection>;
  banner?: IBanner;
  showBanners: boolean;
}


export default function HomeSections({sections, banner, showBanners}: Props) {
  const queryKey = ['home-sections'];
  const queryClient = useQueryClient();
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({queryKey});
    }
  },[]);

    const {
      data,
      isFetching,
      isLoading,
      fetchNextPage,
      hasNextPage
    } = useInfiniteQuery({
      retry: false,
      refetchOnWindowFocus: false,
      initialData: () => {
        return {
          pageParams: [undefined, 1],
          pages: [sections]
        }
      },
      enabled: false,
      queryKey,
      staleTime: 0,
      gcTime: 0,
      queryFn: async ({ pageParam = 1 }) => {
        const result = await apiService.getHomeSections({ page: pageParam as number, pageSize: 4 });
        return result;
      },
      getNextPageParam: (lastPage, allPages, lastPageParam, allPagesParam) => {
        if (!lastPage) return 1;
        return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
      },
      initialPageParam: sections.page ? sections.page + 1 : 1
    });
    const totalData = useMemo(
      () => (data?.pages ? data.pages.map(item => item.data).flat(Infinity) : []),
      [data]
    ) as ISection[];

    const Render = () => {
      const result: any[] = [];
      for (const i in totalData) {
        const eager = Number(i) === 0;
        const s = {...totalData[i], eager};
        result.push(templateComponentsMap(s, `${s.internalName}_${s.id}`)[s.template.id])
      }
        return result;
      }
  
  
    const observer = useRef<IntersectionObserver>();
    const lastElementRef = useCallback(
      (node: HTMLDivElement) => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasNextPage && !isFetching) {
            fetchNextPage();
          }
        }, { threshold: 0.3 });
        if (node) observer.current.observe(node);
      },
      [isLoading, fetchNextPage, hasNextPage, isFetching]
    );
  
  return (
    <div className="flex flex-col relative">
      <Render/>
       {isLoading || isFetching ? <Loader className='mx-auto '/> : hasNextPage ? <div className='absolute left-[50%] bottom-[25%] translate-x-[-50%] h-[0px] w-[0px] z-[100]'  ref={lastElementRef}/> : null}
    </div>
  )

}